import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

type CartCountContextValue = {
  cartCount: number;
  cartHasItems: boolean;
  refreshCartCount: () => Promise<void>;
};

const CartCountContext = createContext<CartCountContextValue | undefined>(undefined);

const getWsUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.host}/api`;
  const base = apiUrl.replace(/\/api\/?$/, '');
  return base.replace(/^http/, 'ws') + '/ws';
};

export const CartCountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [cartCount, setCartCount] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);

  const refreshCartCount = useCallback(async () => {
    if (!user) {
      setCartCount(0);
      return;
    }
    try {
      const response = await api.get('/cart');
      const items = response.data?.items || [];
      const totalItems = items.reduce(
        (sum: number, item: { quantity?: number }) => sum + (item.quantity || 0),
        0
      );
      setCartCount(totalItems);
    } catch (error) {
      setCartCount(0);
    }
  }, [user]);

  useEffect(() => {
    refreshCartCount();
    if (!user) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    const socket = new WebSocket(getWsUrl());
    wsRef.current = socket;

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'identify', role: 'user' }));
      socket.send(JSON.stringify({ type: 'subscribe_cart' }));
    };

    socket.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'cart_count') {
          setCartCount(Number(data.count || 0));
        }
      } catch (error) {
        // ignore
      }
    };

    socket.onclose = () => {
      wsRef.current = null;
    };

    return () => {
      socket.close();
      wsRef.current = null;
    };
  }, [refreshCartCount, user]);

  const value = useMemo(
    () => ({
      cartCount,
      cartHasItems: cartCount > 0,
      refreshCartCount,
    }),
    [cartCount, refreshCartCount]
  );

  return <CartCountContext.Provider value={value}>{children}</CartCountContext.Provider>;
};

export const useCartCount = () => {
  const context = useContext(CartCountContext);
  if (!context) {
    throw new Error('useCartCount must be used within a CartCountProvider');
  }
  return context;
};
